import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type MessagingBroadcastReply = {
  broadcastChannelId: string;
  title: string;
};

type MessagingBroadcastReplyContextValue = {
  broadcastReplyByChannelId: Record<string, MessagingBroadcastReply>;
  clearBroadcastReply: (channelId: string) => void;
  setBroadcastReply: (channelId: string, reply: MessagingBroadcastReply) => void;
};

const MessagingBroadcastReplyContext = createContext<
  MessagingBroadcastReplyContextValue | undefined
>(undefined);

/**
 * Only a channel id survives the navigate into a private reply, and the composer is
 * remounted per channel, so the broadcast being answered is parked here under the id of the
 * channel it will be sent from.
 */
export const MessagingBroadcastReplyProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [broadcastReplyByChannelId, setBroadcastReplyByChannelId] = useState<
    Record<string, MessagingBroadcastReply>
  >({});

  const setBroadcastReply = useCallback(
    (channelId: string, reply: MessagingBroadcastReply) =>
      setBroadcastReplyByChannelId((existing) => ({
        ...existing,
        [channelId]: reply,
      })),
    [],
  );

  const clearBroadcastReply = useCallback(
    (channelId: string) =>
      setBroadcastReplyByChannelId((existing) => {
        if (!existing[channelId]) return existing;

        const { [channelId]: _cleared, ...remaining } = existing;

        return remaining;
      }),
    [],
  );

  const value = useMemo(
    () => ({ broadcastReplyByChannelId, clearBroadcastReply, setBroadcastReply }),
    [broadcastReplyByChannelId, clearBroadcastReply, setBroadcastReply],
  );

  return (
    <MessagingBroadcastReplyContext.Provider value={value}>
      {children}
    </MessagingBroadcastReplyContext.Provider>
  );
};

export const useMessagingBroadcastReply = () => {
  const context = useContext(MessagingBroadcastReplyContext);

  if (!context) {
    throw new Error(
      'useMessagingBroadcastReply requires a MessagingBroadcastReplyProvider',
    );
  }

  return context;
};
